import React, { useState, useRef, useEffect } from "react";
import styles from "../../styles/components/DateRangePicker.module.css";
import { ReactComponent as DownIcon } from "../../assets/icons/down.svg";

const PRESETS = [
  { id: "all",       label: "Todo el tiempo" },
  { id: "today",     label: "Hoy" },
  { id: "yesterday", label: "Ayer" },
  { id: "thisWeek",  label: "Esta semana" },
  { id: "thisMonth", label: "Este mes" },
  { id: "thisYear",  label: "Este año" },
  { id: "lastWeek",  label: "Semana pasada" },
  { id: "lastMonth", label: "Mes pasado" },
  { id: "lastYear",  label: "Año pasado" },
];

const DAY_NAMES = ["L", "M", "M", "J", "V", "S", "D"];
const MONTH_NAMES = [
  "Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre",
];

function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}
function endOfWeek(date) {
  const d = startOfWeek(date);
  d.setDate(d.getDate() + 6);
  d.setHours(23, 59, 59, 999);
  return d;
}
function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}
function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}
function startOfYear(date) {
  return new Date(date.getFullYear(), 0, 1);
}
function endOfYear(date) {
  return new Date(date.getFullYear(), 11, 31);
}
function addMonths(date, n) {
  const d = new Date(date);
  d.setDate(1);
  d.setMonth(d.getMonth() + n);
  return d;
}
function isSameDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function isBetween(d, start, end) {
  if (
    !(d instanceof Date) || Number.isNaN(d.getTime()) ||
    !(start instanceof Date) || Number.isNaN(start.getTime()) ||
    !(end instanceof Date) || Number.isNaN(end.getTime())
  ) {
    return false;
  }
  const t = d.getTime();
  return t > start.getTime() && t < end.getTime();
}

function getPresetRange(id) {
  const now = new Date();
  switch (id) {
    case "all": return [null, null];
    case "today": {
      const s = new Date(now); s.setHours(0, 0, 0, 0);
      const e = new Date(now); e.setHours(23, 59, 59, 999);
      return [s, e];
    }
    case "yesterday": {
      const s = new Date(now); s.setDate(now.getDate() - 1); s.setHours(0, 0, 0, 0);
      const e = new Date(s);   e.setHours(23, 59, 59, 999);
      return [s, e];
    }
    case "thisWeek":  return [startOfWeek(now), endOfWeek(now)];
    case "thisMonth": return [startOfMonth(now), endOfMonth(now)];
    case "thisYear":  return [startOfYear(now), endOfYear(now)];
    case "lastWeek": {
      const prev = new Date(now);
      prev.setDate(now.getDate() - 7);
      return [startOfWeek(prev), endOfWeek(prev)];
    }
    case "lastMonth": {
      const prev = addMonths(now, -1);
      return [startOfMonth(prev), endOfMonth(prev)];
    }
    case "lastYear": {
      const prev = new Date(now.getFullYear() - 1, 0, 1);
      return [startOfYear(prev), endOfYear(prev)];
    }
    default: return [null, null];
  }
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year, month) {

  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
}

function CalendarMonth({ year, month, startDate, endDate, hoverDate, selecting, onDayClick, onDayHover }) {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const rangeEnd = selecting && hoverDate ? hoverDate : endDate;

  return (
    <div className={styles.calendarMonth}>
      <div className={styles.calendarMonthTitle}>
        {MONTH_NAMES[month]} {year}
      </div>
      <div className={styles.calendarGrid}>
        {DAY_NAMES.map((n, i) => (
          <div key={i} className={styles.dayName}>{n}</div>
        ))}
        {cells.map((date, i) => {
          if (!date) return <div key={`e-${i}`} />;

          const isStart = isSameDay(date, startDate);
          const isEnd = rangeEnd && isSameDay(date, rangeEnd);
          const inRange = startDate && rangeEnd && isBetween(date, startDate, rangeEnd);
          const isToday = isSameDay(date, new Date());

          return (
            <button
              key={date.toISOString()}
              type="button"
              className={[
                styles.day,
                isStart || isEnd ? styles.daySelected : "",
                inRange ? styles.dayInRange : "",
                isToday && !isStart && !isEnd ? styles.dayToday : "",
              ].filter(Boolean).join(" ")}
              onClick={() => onDayClick(date)}
              onMouseEnter={() => onDayHover && onDayHover(date)}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function formatLabel(startDate, endDate, preset) {
  if (preset) {
    const p = PRESETS.find(p => p.id === preset);
    if (p) return p.label;
  }
  if (!startDate) return "Este mes";
  const fmt = (d) =>
    `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
  if (!endDate || isSameDay(startDate, endDate)) return fmt(startDate);
  return `${fmt(startDate)} - ${fmt(endDate)}`;
}

export default function DateRangePicker({ onChange }) {
  const now = new Date();
  const [open, setOpen] = useState(false);
  const [leftYear, setLeftYear] = useState(now.getFullYear());
  const [leftMonth, setLeftMonth] = useState(now.getMonth());

  const [startDate, setStartDate] = useState(startOfMonth(now));
  const [endDate, setEndDate] = useState(endOfMonth(now));
  const [preset, setPreset] = useState("thisMonth");

  const [draftStart, setDraftStart] = useState(startOfMonth(now));
  const [draftEnd, setDraftEnd] = useState(endOfMonth(now));
  const [draftPreset, setDraftPreset] = useState("thisMonth");
  const [selecting, setSelecting] = useState(false);
  const [hoverDate, setHoverDate] = useState(null);

  const wrapperRef = useRef(null);

  useEffect(() => {
    function handleOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  const rightYear = leftMonth === 11 ? leftYear + 1 : leftYear;
  const rightMonth = leftMonth === 11 ? 0 : leftMonth + 1;

  function handleOpen() {

    setDraftStart(startDate);
    setDraftEnd(endDate);
    setDraftPreset(preset);
    setSelecting(false);
    setHoverDate(null);
    setOpen(true);
  }

  function handlePresetClick(id) {
    const [s, e] = getPresetRange(id);
    setDraftStart(s);
    setDraftEnd(e);
    setDraftPreset(id);
    setSelecting(false);
    setHoverDate(null);

    if (s) {
      setLeftYear(s.getFullYear());
      setLeftMonth(s.getMonth());
    }

    setStartDate(s);
    setEndDate(e);
    setPreset(id);
    onChange?.({ startDate: s, endDate: e, preset: id });
    setOpen(false);
  }

  function handleDayClick(date) {
    if (!selecting) {
      setDraftStart(date);
      setDraftEnd(null);
      setDraftPreset(null);
      setSelecting(true);
    } else {
      let s = draftStart;
      let e = date;
      if (e < s) { [s, e] = [e, s]; }
      setDraftStart(s);
      setDraftEnd(e);
      setDraftPreset(null);
      setSelecting(false);
      setHoverDate(null);
    }
  }

  function handleApply() {
    setStartDate(draftStart);
    setEndDate(draftEnd);
    setPreset(draftPreset);
    onChange?.({ startDate: draftStart, endDate: draftEnd, preset: draftPreset });
    setOpen(false);
  }

  function handleCancel() {
    setOpen(false);
  }

  function prevMonth() {
    if (leftMonth === 0) { setLeftMonth(11); setLeftYear(y => y - 1); }
    else setLeftMonth(m => m - 1);
  }
  function nextMonth() {
    if (leftMonth === 11) { setLeftMonth(0); setLeftYear(y => y + 1); }
    else setLeftMonth(m => m + 1);
  }

  const label = formatLabel(startDate, endDate, preset);

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        type="button"
        className={[styles.trigger, open ? styles.triggerOpen : ""].join(" ")}
        onClick={open ? () => setOpen(false) : handleOpen}
      >
        <span>{label}</span>
        <DownIcon className={[styles.chevron, open ? styles.chevronUp : ""].join(" ")} />
      </button>

      {open && (
        <div className={styles.dropdown}>

          <div className={styles.dropdownBody}>

            <div className={styles.presets}>
              <div className={styles.presetsTitle}>Fechas</div>
              {PRESETS.map(p => (
                <button
                  key={p.id}
                  type="button"
                  className={[styles.presetBtn, draftPreset === p.id ? styles.presetBtnActive : ""].join(" ")}
                  onClick={() => handlePresetClick(p.id)}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <div className={styles.dropdownRight}>
              <div className={styles.calendars}>
                <div className={styles.calendarNav}>
                  <button type="button" className={styles.navBtn} onClick={prevMonth}>‹</button>
                </div>

                <CalendarMonth
                  year={leftYear}
                  month={leftMonth}
                  startDate={draftStart}
                  endDate={draftEnd}
                  hoverDate={hoverDate}
                  selecting={selecting}
                  onDayClick={handleDayClick}
                  onDayHover={selecting ? setHoverDate : null}
                />
                <CalendarMonth
                  year={rightYear}
                  month={rightMonth}
                  startDate={draftStart}
                  endDate={draftEnd}
                  hoverDate={hoverDate}
                  selecting={selecting}
                  onDayClick={handleDayClick}
                  onDayHover={selecting ? setHoverDate : null}
                />

                <div className={styles.calendarNav}>
                  <button type="button" className={styles.navBtn} onClick={nextMonth}>›</button>
                </div>
              </div>

              <div className={styles.footer}>
                <button type="button" className={styles.cancelBtn} onClick={handleCancel}>Cancelar</button>
                <button type="button" className={styles.applyBtn} onClick={handleApply}>Aplicar</button>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
